import { Button, Checkbox, Td, Tr } from "@chakra-ui/react";
import { isEmpty } from "lodash";
import { FunctionComponent, ReactElement } from "react";
import { Link } from "react-router-dom";

// Types
import { DataTableColumn, KVP } from "../../../utils/types";

// Utils
import { convertDateToUTCDate } from "../../../utils/helpers";

interface DataTableRowProps {
	row: KVP;
	columns: Array<DataTableColumn>;
	isSelected: boolean;
	selectionEnabled: boolean;

	updateSelected: (rowId: string, isChecked: boolean) => void;
}

/**
 * Given a row, a key-value pair, maps the data to table rows using the provided column mapping. Also
 * handles selecting a row for other provided actions
 * @param props typeof: DataTableRowProps
 */
const DataTableRow: FunctionComponent<DataTableRowProps> = (props: DataTableRowProps) => {
	const { row, columns, isSelected, selectionEnabled, updateSelected } = props;

	/**
	 * Event handler to manage selecting a row
	 * @param event typeof: React.ChangeEvent<HTMLInputElement>
	 */
	const handleRowChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
		const isChecked: boolean = event.target.checked;
		updateSelected(row["id"].toString(), isChecked);
	};

	/**
	 * Helper function to handle row background color CSS
	 * @returns backgroundColor CSS Value
	 */
	const getRowBackground = (): string => (isSelected ? "#ffb88964 !important" : "inherit");

	/**
	 * Maps row data to a generic cell, if a more specified cell type is not provided
	 * @param column typeof: DataTableColumn, which column does this cell map to?
	 * @returns <Td /> element
	 */
	const getGenericTableCell = (column: DataTableColumn): ReactElement => {
		let cellContents: string | ReactElement;

		// if Cell is empty and doesn't have custom mapping...
		if (isEmpty(row[column.id]?.toString()) && !column.valueMap) {
			cellContents = "";
		}
		// If the cell has custom mapping, supersede all other variables...
		else if (column.valueMap && typeof column.valueMap === "function") {
			if (column.allData) cellContents = column.valueMap(row) as ReactElement;
			else cellContents = column.valueMap(row[column.id]) as ReactElement;
		}
		// Handle Date and DateTime columns separately...
		else if (column.isDate) {
			cellContents = convertDateToUTCDate(new Date(row[column.id])).toLocaleDateString();
		} else if (column.isDateTime) {
			cellContents = new Date(row[column.id]).toLocaleString();
		}
		// If unspecified type, insert raw data into cell
		else cellContents = row[column.id].toString();

		return (
			<Td
				key={`${row.id}${column.id}`}
				paddingInlineStart={2}
				paddingInlineEnd={2}
				style={{
					width: column.width,
					minWidth: column.minWidth,
					maxWidth: column.maxWidth,
					whiteSpace: column.truncate ? "nowrap" : "break-spaces"
				}}
				display={column.isHidden ? "none" : "table-cell"}
				backgroundColor={getRowBackground()}>
				{cellContents}
			</Td>
		);
	};

	/**
	 * Maps row data to variable cell types, uses column mapping to determine which
	 * cell type we need to map, or generic if unspecified
	 * @param column typeof: DataTableColumn, which column does this cell map to?
	 * @returns typeof: ReactElement, <Td /> element
	 */
	const mapRowToColumn = (column: DataTableColumn): ReactElement => {
		if (column.linkable) {
			return (
				<Td
					key={`${row.id}${column.id}`}
					paddingInlineStart={2}
					paddingInlineEnd={2}
					style={{
						paddingTop: "0px",
						paddingBottom: "0px",
						width: column.width,
						minWidth: column.minWidth,
						maxWidth: column.maxWidth,
						whiteSpace: column.truncate ? "nowrap" : "break-spaces"
					}}
					display={column.isHidden ? "none" : "table-cell"}
					backgroundColor={getRowBackground()}>
					{column.valueMap && typeof column.valueMap === "function" ? (
						column.allData ? (
							column.valueMap(row)
						) : (
							column.valueMap(row[column.id])
						)
					) : (
						<Button
							as={Link}
							variant="ghost"
							size="sm"
							style={{ color: "orange.600", background: "transparent" }}
							_hover={{ textDecoration: "underline" }}
							to={`${window.location.pathname}/${row[column.id]}`}
							state={{ from: window.location.pathname + window.location.search }}>
							{row[column.id]}
						</Button>
					)}
				</Td>
			);
		} else return getGenericTableCell(column);
	};

	return (
		<Tr key={row["id"]}>
			{selectionEnabled && (
				<Td
					paddingInlineStart={2}
					paddingInlineEnd={2}
					style={{
						padding: "8px"
					}}
					backgroundColor={getRowBackground()}>
					<Checkbox colorScheme="orange" isChecked={isSelected} onChange={(event) => handleRowChecked(event)} />
				</Td>
			)}
			{columns.map((column: DataTableColumn) => mapRowToColumn(column))}
		</Tr>
	);
};

export default DataTableRow;
